import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, Loader2, Search } from 'lucide-react';

interface NominatimResult {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
  type: string;
  address?: {
    city?: string;
    town?: string;
    village?: string;
    county?: string;
    state?: string;
    country?: string;
  };
}

interface LocationValue {
  label: string;
  lat: number;
  lon: number;
}

interface LocationSearchInputProps {
  value: string;
  onChange: (location: LocationValue) => void;
  placeholder?: string;
  required?: boolean;
  className?: string;
  label?: string;
}

const LocationSearchInput: React.FC<LocationSearchInputProps> = ({
  value,
  onChange,
  placeholder = 'e.g. Ibadan, Oyo State, Nigeria',
  required = false,
  className = '',
  label = 'Location *:',
}) => {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState<NominatimResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIdx, setHighlightedIdx] = useState(-1);
  const [hasSelected, setHasSelected] = useState(false);

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync external value changes
  useEffect(() => {
    if (value && value !== query) {
      setQuery(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setHighlightedIdx(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchLocations = useCallback(async (searchQuery: string) => {
    if (searchQuery.trim().length < 3) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    setIsLoading(true);
    try {
      // NOTE: Do NOT set User-Agent header in browser — it's a forbidden header
      // Nominatim accepts browser requests with the browser's own User-Agent
      const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
        searchQuery
      )}&format=json&limit=8&addressdetails=1&accept-language=en`;

      const res = await fetch(url);
      if (!res.ok) throw new Error(`Nominatim HTTP ${res.status}`);
      const data: NominatimResult[] = await res.json();
      setResults(data);
      setIsOpen(data.length > 0);
      setHighlightedIdx(-1);
    } catch (err) {
      console.error('[LocationSearch] Fetch error:', err);
      setResults([]);
      setIsOpen(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    setHasSelected(false);

    // Debounce — 400ms
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      fetchLocations(val);
    }, 400);
  };

  const handleSelect = (result: NominatimResult) => {
    const label = result.display_name;
    setQuery(label);
    setHasSelected(true);
    setIsOpen(false);
    setResults([]);
    onChange({
      label,
      lat: parseFloat(result.lat),
      lon: parseFloat(result.lon),
    });
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIdx((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIdx((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIdx >= 0 && results[highlightedIdx]) {
        handleSelect(results[highlightedIdx]);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setHighlightedIdx(-1);
    }
  };

  // Build a compact display name from the full display_name
  const getCompactName = (result: NominatimResult): string => {
    const addr = result.address;
    if (!addr) return result.display_name;
    const city = addr.city || addr.town || addr.village || '';
    const state = addr.state || addr.county || '';
    const country = addr.country || '';
    const parts = [city, state, country].filter(Boolean);
    return parts.length >= 2 ? parts.join(', ') : result.display_name;
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {label && (
        <label className="block text-xs font-extrabold text-gray-700 mb-1">
          {label}
        </label>
      )}

      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
          {isLoading ? (
            <Loader2 className="w-4 h-4 text-emerald-500 animate-spin" />
          ) : hasSelected ? (
            <MapPin className="w-4 h-4 text-emerald-600" />
          ) : (
            <Search className="w-4 h-4 text-gray-400" />
          )}
        </div>

        <input
          ref={inputRef}
          type="text"
          required={required}
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (results.length > 0) setIsOpen(true);
          }}
          placeholder={placeholder}
          autoComplete="off"
          className={`w-full pl-9 pr-4 py-3 border rounded-xl text-sm font-semibold transition-all focus:outline-none focus:ring-2 ${
            hasSelected
              ? 'border-emerald-400 bg-emerald-50/30 focus:ring-emerald-400'
              : 'border-gray-300 bg-white focus:ring-emerald-500'
          }`}
        />

        {isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="w-3 h-3 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
          <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-100 flex items-center gap-1.5">
            <MapPin className="w-3 h-3 text-emerald-500" />
            <span className="text-[10px] font-extrabold text-gray-500 uppercase tracking-wide">
              {results.length} Location{results.length > 1 ? 's' : ''} Found
            </span>
          </div>

          <ul className="max-h-64 overflow-y-auto divide-y divide-gray-50">
            {results.map((result, idx) => {
              const compact = getCompactName(result);
              const isHighlighted = idx === highlightedIdx;

              return (
                <li
                  key={result.place_id}
                  onMouseEnter={() => setHighlightedIdx(idx)}
                  onClick={() => handleSelect(result)}
                  className={`px-4 py-2.5 cursor-pointer transition-colors flex items-start gap-3 ${
                    isHighlighted ? 'bg-emerald-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <MapPin
                    className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${
                      isHighlighted ? 'text-emerald-600' : 'text-gray-400'
                    }`}
                  />
                  <div className="min-w-0">
                    <p
                      className={`text-sm font-semibold truncate ${
                        isHighlighted ? 'text-emerald-900' : 'text-gray-800'
                      }`}
                    >
                      {compact}
                    </p>
                    {compact !== result.display_name && (
                      <p className="text-[11px] text-gray-400 truncate mt-0.5">
                        {result.display_name}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>

          <div className="px-3 py-1.5 bg-gray-50 border-t border-gray-100">
            <p className="text-[10px] text-gray-400">
              Powered by{' '}
              <a
                href="https://www.openstreetmap.org"
                target="_blank"
                rel="noreferrer"
                className="underline hover:text-emerald-600"
              >
                OpenStreetMap
              </a>
            </p>
          </div>
        </div>
      )}

      {/* No results hint */}
      {isOpen && !isLoading && results.length === 0 && query.length >= 3 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg px-4 py-3 text-xs text-gray-500 font-semibold">
          No locations found for "{query}". Try a more specific search.
        </div>
      )}
    </div>
  );
};

export default LocationSearchInput;
